import React, { useContext, useState } from 'react';
import { Form, Input, Button, Select, message } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import api from '../../../api';
import { Context } from '../../../context/AuthContext';
import MonetaryConverter from '../../../helpers/monetaryConverter';
import AnimatedMonetaryConverter from '../../../helpers/animatedMonetaryConverter';
import './filter.css';

function Filter(props) {

    const { userData } = useContext(Context);

    const [loading, setLoading] = useState(false)
    const [filters, setFilters] = useState(false)
    const [editAnnualGoal, setEditAnnualGoal] = useState(false)
    const [annualGoalValue, setAnnualGoalValue] = useState('')
    const [annualGoalLoading, setAnnualGoalLoading] = useState(false)
    const [editMonthlyGoal, setEditMonthlyGoal] = useState(false)
    const [monthlyGoalValue, setMonthlyGoalValue] = useState('')
    const [monthlyGoalLoading, setMonthlyGoalLoading] = useState(false)

    // form
    const [form] = Form.useForm();

    const monthNames = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

    // Gerar opcoes de mes
    function getMonthOptions() {
        const months = [];
        const currentYear = new Date().getFullYear();
        for (let i = 0; i < 12; i++) {
            const value = `${String(i + 1).padStart(2, '0')}/${currentYear}`;
            months.push({ label: `${monthNames[i]}/${currentYear}`, value });
        }
        return months;
    }

    // Retorna os nomes dos meses selecionados no filtro
    function getSelectedMonthsLabel() {
        if (props.filters.month && props.filters.month.length > 0) {
            const months = Array.isArray(props.filters.month) ? props.filters.month : [props.filters.month];
            const labels = months.map(m => {
                const monthNumber = parseInt(m.split('/')[0], 10);
                return monthNames[monthNumber - 1];
            });
            return labels.join(', ');
        }
        return null;
    }

    // Retorna os nomes dos vendedores selecionados no filtro
    function getSelectedResponsiblesLabel() {
        if (props.filters.responsible && props.filters.responsible.length > 0 && props.users) {
            const responsibles = Array.isArray(props.filters.responsible) ? props.filters.responsible : [props.filters.responsible];
            const labels = responsibles.map(r => {
                const user = props.users.find(u => u._id === r);
                return user ? user.name : null;
            }).filter(Boolean);
            return labels.join(', ');
        }
        return null;
    }

    // Monta o label do total com os filtros aplicados
    function getTotalLabel() {
        const parts = [];
        if (getSelectedMonthsLabel()) parts.push(getSelectedMonthsLabel());
        if (getSelectedResponsiblesLabel()) parts.push(getSelectedResponsiblesLabel());

        if (parts.length > 0) {
            return `Total (${parts.join(' - ')})`;
        }
        return 'Total';
    }

    // Verifica se há apenas um vendedor selecionado (para editar meta individual)
    function getSingleSelectedResponsible() {
        if (props.filters.responsible) {
            const responsibles = Array.isArray(props.filters.responsible) ? props.filters.responsible : [props.filters.responsible];
            if (responsibles.length === 1) {
                return responsibles[0];
            }
        }
        return null;
    }

    // Verifica se há apenas um mês selecionado (para editar meta mensal)
    function getSingleSelectedMonth() {
        if (props.filters.month) {
            const months = Array.isArray(props.filters.month) ? props.filters.month : [props.filters.month];
            if (months.length === 1) {
                return months[0];
            }
        }
        return null;
    }

    const onFinish = async values => {
        props.setLoadSalesGoals(true)

        let url = "/sales-goals?";
        let newFilters = {}

        if (values.month && values.month.length > 0) {
            url = url.concat(`&month=${values.month.join(',')}`)
            newFilters.month = values.month
        }

        if (values.operator) {
            url = url.concat(`&operator=${values.operator}`)
            newFilters.operator = values.operator
        }

        if (values.responsible && values.responsible.length > 0) {
            url = url.concat(`&responsible=${values.responsible.join(',')}`)
            newFilters.responsible = values.responsible
        }

        if (values.search) {
            url = url.concat(`&search=${encodeURIComponent(values.search)}`)
            newFilters.search = values.search
        }

        const { data } = await api.get(url);
        props.setFilters(newFilters)
        props.setSalesGoals(data.salesGoals)
        props.setTotalSalesGoals(data.total)
        props.setLoadSalesGoals(false)
        setFilters(true)
    };

    async function clearFilter() {
        form.resetFields();
        props.setLoadSalesGoals(true)

        const { data } = await api.get("/sales-goals");
        props.setSalesGoals(data.salesGoals)
        props.setTotalSalesGoals(data.total)

        props.setLoadSalesGoals(false)
        setFilters(false)
        props.setFilters({})
    }

    function changeAnnualGoalValue(value) {
        let v = value;
        v = v.replace(/\D/g, "")
        v = v.replace(/(\d{1})(\d{14})$/, "$1.$2")
        v = v.replace(/(\d{1})(\d{11})$/, "$1.$2")
        v = v.replace(/(\d{1})(\d{8})$/, "$1.$2")
        v = v.replace(/(\d{1})(\d{5})$/, "$1.$2")
        v = v.replace(/(\d{1})(\d{1,2})$/, "$1,$2")
        setAnnualGoalValue("R$ " + v)
    }

    function changeMonthlyGoalValue(value) {
        let v = value;
        v = v.replace(/\D/g, "")
        v = v.replace(/(\d{1})(\d{14})$/, "$1.$2")
        v = v.replace(/(\d{1})(\d{11})$/, "$1.$2")
        v = v.replace(/(\d{1})(\d{8})$/, "$1.$2")
        v = v.replace(/(\d{1})(\d{5})$/, "$1.$2")
        v = v.replace(/(\d{1})(\d{1,2})$/, "$1,$2")
        setMonthlyGoalValue("R$ " + v)
    }

    async function saveAnnualGoal() {
        setAnnualGoalLoading(true)
        const currentYear = new Date().getFullYear();
        const numericValue = annualGoalValue.replace("R$ ", '').replace(/\./g, '').replace(',', '.')
        const singleResponsible = getSingleSelectedResponsible();

        await api.put(`/sales-goals/annual-goal/${currentYear}`, {
            targetValue: numericValue,
            responsible: singleResponsible
        }).then(async () => {
            message.success('Meta anual atualizada!')
            setEditAnnualGoal(false)
            setAnnualGoalLoading(false)
            // Recarrega summary com os filtros atuais
            let url = `/sales-goals/summary/${currentYear}`;
            const params = [];
            if (props.filters.month && props.filters.month.length > 0) {
                params.push(`month=${props.filters.month.join(',')}`);
            }
            if (props.filters.responsible && props.filters.responsible.length > 0) {
                params.push(`responsible=${props.filters.responsible.join(',')}`);
            }
            if (params.length > 0) {
                url += `?${params.join('&')}`;
            }
            const { data } = await api.get(url);
            props.setSummary(data)
        }).catch(() => {
            message.error('Erro ao atualizar meta anual.')
            setAnnualGoalLoading(false)
        })
    }

    async function saveMonthlyGoal() {
        setMonthlyGoalLoading(true)
        const singleMonth = getSingleSelectedMonth();
        const numericValue = monthlyGoalValue.replace("R$ ", '').replace(/\./g, '').replace(',', '.')
        const singleResponsible = getSingleSelectedResponsible();

        // Formato do mês para a URL (substitui / por -)
        const monthParam = singleMonth.replace('/', '-');

        await api.put(`/sales-goals/monthly-goal/${monthParam}`, {
            targetValue: numericValue,
            responsible: singleResponsible
        }).then(async () => {
            message.success('Meta mensal atualizada!')
            setEditMonthlyGoal(false)
            setMonthlyGoalLoading(false)
            // Recarrega summary com os filtros atuais
            const currentYear = new Date().getFullYear();
            let url = `/sales-goals/summary/${currentYear}`;
            const params = [];
            if (props.filters.month && props.filters.month.length > 0) {
                params.push(`month=${props.filters.month.join(',')}`);
            }
            if (props.filters.responsible && props.filters.responsible.length > 0) {
                params.push(`responsible=${props.filters.responsible.join(',')}`);
            }
            if (params.length > 0) {
                url += `?${params.join('&')}`;
            }
            const { data } = await api.get(url);
            props.setSummary(data)
        }).catch(() => {
            message.error('Erro ao atualizar meta mensal.')
            setMonthlyGoalLoading(false)
        })
    }

    return (
        <>
            <div className={`customers-filter`} style={{marginBottom: "0 !important"}}>
                <Form form={form} layout="vertical" onFinish={onFinish}>

                    <Form.Item label="Mês" name="month">
                        <Select
                            mode="multiple"
                            showSearch
                            style={{ minWidth: 180 }}
                            placeholder="Todos"
                            optionFilterProp="children"
                            maxTagCount={2}
                            maxTagPlaceholder={(omittedValues) => `+${omittedValues.length}`}
                            filterOption={(input, option) =>
                                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                            }
                        >
                            {getMonthOptions().map((m) => (
                                <Select.Option key={m.value} value={m.value}>{m.label}</Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item label="Operadora" name="operator">
                        <Input placeholder="Operadora" style={{ width: 150 }} />
                    </Form.Item>

                    {(userData.roles.includes('admin') || userData.roles.includes('specialist') || userData.roles.includes('supervisor')) &&
                        <Form.Item label="Vendedor" name="responsible">
                            <Select
                                mode="multiple"
                                showSearch
                                style={{ minWidth: 180 }}
                                placeholder="Todos"
                                optionFilterProp="children"
                                maxTagCount={2}
                                maxTagPlaceholder={(omittedValues) => `+${omittedValues.length}`}
                                filterOption={(input, option) =>
                                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                            >
                                {props.users &&
                                    props.users.map((user) => (
                                        <Select.Option key={user._id} value={user._id}>{user.name}</Select.Option>
                                    ))
                                }
                            </Select>
                        </Form.Item>
                    }

                    <Form.Item label="Palavra chave" name="search">
                        <Input placeholder="Empresa, Listagem" />
                    </Form.Item>

                    <Form.Item style={{ marginRight: 0 }} label=" ">
                        <Button type="default" disabled={!filters} onClick={() => clearFilter()} style={{ width: 140, marginRight: 10 }}>Limpar Filtros</Button>
                        <Button type="primary" htmlType="submit" loading={loading} style={{ width: 140 }}>Filtrar Metas</Button>
                    </Form.Item>

                </Form>
            </div>

            <div className="sales-goals-summary">
                <div className="summary-item vertical">
                    <span className="summary-label">{getTotalLabel()}</span>
                    <span className="summary-value current"><AnimatedMonetaryConverter quantity={props.summary.total} class="plan-price" /></span>
                </div>

                {/* Meta Mensal - só exibe quando há filtro de mês */}
                {props.filters.month && props.filters.month.length > 0 && (
                    <>
                        <div className="summary-divider"></div>
                        <div className="summary-item vertical">
                            <span className="summary-label">
                                Meta Mensal{getSingleSelectedMonth() ? ` (${getSelectedMonthsLabel()})` : ''}
                                {getSingleSelectedResponsible() && getSingleSelectedMonth() ? ` - ${getSelectedResponsiblesLabel()}` : ''}
                                {userData.roles.includes('admin') && getSingleSelectedMonth() && getSingleSelectedResponsible() && !editMonthlyGoal &&
                                    <Button type="text" size="small" icon={<EditOutlined />} onClick={() => setEditMonthlyGoal(true)} className="summary-edit-btn">Editar</Button>
                                }
                            </span>
                            {editMonthlyGoal
                                ? <div className="summary-edit-monthly">
                                    <Input
                                        value={monthlyGoalValue}
                                        onChange={(e) => changeMonthlyGoalValue(e.target.value)}
                                        style={{ width: 120 }}
                                        placeholder="R$ 0,00"
                                        size="small"
                                    />
                                    <Button type="primary" size="small" onClick={saveMonthlyGoal} loading={monthlyGoalLoading} style={{ marginLeft: 5 }}>Salvar</Button>
                                    <Button size="small" onClick={() => setEditMonthlyGoal(false)} style={{ marginLeft: 5 }}>Cancelar</Button>
                                </div>
                                : <>
                                    <div className="summary-values">
                                        <span className="summary-value monthly"><AnimatedMonetaryConverter quantity={props.summary.monthlyGoal} class="plan-price" /></span>
                                        <span className="summary-separator">/</span>
                                        <span className="summary-value remaining"><AnimatedMonetaryConverter quantity={props.summary.monthlyRemaining} class="plan-price" /></span>
                                    </div>
                                    <span className="summary-hint">meta / falta bater</span>
                                </>
                            }
                        </div>
                    </>
                )}

                <div className="summary-divider"></div>
                <div className="summary-item vertical">
                    <span className="summary-label">
                        Meta Anual{getSingleSelectedResponsible() ? ` (${getSelectedResponsiblesLabel()})` : ''}
                        {userData.roles.includes('admin') && getSingleSelectedResponsible() && !editAnnualGoal &&
                            <Button type="text" size="small" icon={<EditOutlined />} onClick={() => setEditAnnualGoal(true)} className="summary-edit-btn">Editar</Button>
                        }
                    </span>
                    {editAnnualGoal
                        ? <div className="summary-edit-annual">
                            <Input
                                value={annualGoalValue}
                                onChange={(e) => changeAnnualGoalValue(e.target.value)}
                                style={{ width: 120 }}
                                placeholder="R$ 0,00"
                                size="small"
                            />
                            <Button type="primary" size="small" onClick={saveAnnualGoal} loading={annualGoalLoading} style={{ marginLeft: 5 }}>Salvar</Button>
                            <Button size="small" onClick={() => setEditAnnualGoal(false)} style={{ marginLeft: 5 }}>Cancelar</Button>
                        </div>
                        : <>
                            <div className="summary-values">
                                <span className="summary-value yarly"><AnimatedMonetaryConverter quantity={props.summary.annualGoal} class="plan-price" /></span>
                                <span className="summary-separator">/</span>
                                <span className="summary-value remaining"><AnimatedMonetaryConverter quantity={props.summary.remaining} class="plan-price" /></span>
                            </div>
                            <span className="summary-hint">meta / falta bater</span>
                        </>
                    }
                </div>
            </div>
        </>
    )
}

export default Filter;
